// the key shown under the dimension picker: the heatmap's colours, and the values they stand for. Also the summary of
// the dimension the scorecard shows
import { DimensionSummary } from '../../epm/epm-scorecard-model';

export interface HeatmapLegend {
    gradient: string; // a CSS gradient running from no heat to the most
    scale: string[]; // values at the start, middle and end of the gradient
    note: string;
}

// enough of a yFiles GradientStop: its colour's channels are 0 to 255, alpha included
export interface LegendStop {
    offset: number;
    color: { r: number; g: number; b: number; a: number };
}

const rgba = ({ r, g, b, a }: LegendStop['color']) => `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${+(a / 255).toFixed(3)})`;
const sorted = (stops: Iterable<LegendStop>) => [...stops].sort((x, y) => x.offset - y.offset);

export const gradientToCss = (stops: Iterable<LegendStop>): string => {
    const parts = sorted(stops).map((s) => `${rgba(s.color)} ${Math.round(s.offset * 100)}%`);
    return `linear-gradient(to right, ${parts.join(', ')})`;
};

// the gradient's colour channels at a point from 0 to 1, blending the stops either side
export const channelsAt = (stops: Iterable<LegendStop>, t: number): LegendStop['color'] | undefined => {
    const list = sorted(stops);
    if (!list.length) return undefined;
    const at = Math.min(1, Math.max(0, t));
    const after = list.findIndex((s) => s.offset >= at);
    if (after <= 0) return list[after < 0 ? list.length - 1 : 0].color;
    const a = list[after - 1], b = list[after];
    const f = (at - a.offset) / (b.offset - a.offset || 1);
    const mix = (x: number, y: number) => x + (y - x) * f;
    return { r: mix(a.color.r, b.color.r), g: mix(a.color.g, b.color.g), b: mix(a.color.b, b.color.b), a: mix(a.color.a, b.color.a) };
};

export const colourAt = (stops: Iterable<LegendStop>, t: number): string => {
    const c = channelsAt(stops, t);
    return c ? rgba(c) : 'transparent';
};

const DARK_TEXT = '#1f2937', LIGHT_TEXT = '#ffffff';

// whichever of dark or white text contrasts more with an opaque colour (WCAG relative luminance)
export const readableTextOn = ({ r, g, b }: LegendStop['color']): string => {
    const linear = (c: number) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const luminance = 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
    const contrast = (l1: number, l2: number) => (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
    // the dark text's luminance, worked out once: #1f2937
    const dark = 0.2126 * linear(0x1f) + 0.7152 * linear(0x29) + 0.0722 * linear(0x37);
    return contrast(luminance, dark) >= contrast(luminance, 1) ? DARK_TEXT : LIGHT_TEXT;
};

// the steps with the most of a dimension, highest first and coloured as in the heatmap, for the scorecard
export const createDimensionSummary = (
    dimension: string,
    steps: { key: string; name: string; value: number; label: string }[],
    stops: Iterable<LegendStop>,
    scale: number,
    total?: string,
    limit = 10,
): DimensionSummary => ({
    dimension,
    total,
    steps: steps
        .filter((s) => s.value > 0)
        .sort((x, y) => y.value - x.value)
        .slice(0, limit)
        .map((s) => {
            const share = scale > 0 ? Math.min(1, s.value / scale) : 0;
            return { key: s.key, name: s.name, label: s.label, share, colour: colourAt(stops, share) };
        }),
});

// heat is a value as a share of the largest, step or connection, so the scale runs from nothing to the largest
export const createHeatmapLegend = (
    stops: Iterable<LegendStop>,
    max: number,
    format: (value: number) => string,
    dimension: string,
): HeatmapLegend => ({
    gradient: gradientToCss(stops),
    scale: max > 0 ? [format(0), format(max / 2), format(max)] : [],
    note:
        max > 0
            ? `Colours show the ${dimension.toLowerCase()} at each step and along each connection, where a step's value is shared between the routes leaving it.`
            : `No steps have any ${dimension.toLowerCase()} in the models shown.`,
});
